import './Table.scss';

import cn from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import Button from '@/common/components/atoms/Button/Button';
import Price from '@/common/components/blocks/Price/Price';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import { BREAKPOINTS, NULL_VALUE, ROUTE_PRODUCT } from '@/common/constants';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useAuth } from '@/common/contexts/AuthContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import addLinksToBlockElements from '@/common/helpers/addLinksToBlockElements';
import withPriceAndStock from '@/common/hoc/products/withPriceAndStock';
import Stocks from '@/ebat/components/blocks/Common/Stocks/Stocks';
import ToggleWishlist from '@/ebat/components/blocks/Products/ToggleWishlist/ToggleWishlist';
import { ReactComponent as CartIcon } from '@/materiaux/assets/icons/basket.svg';

import CONFIG from './config';
import { TableDefaultProps, TablePropTypes } from './Table.proptypes';

// TODO: WARNING Table has addToCart methods, so it could be used only in a case of add to cart
const Table = ({
  addToCartOptions,
  brand,
  className,
  companyAvailability,
  configurationsSortables,
  customerPrice,
  disabled,
  discountPrice,
  /* eslint-disable no-unused-vars */
  hasPrice, // withPriceAndStock
  hasStock, // withPriceAndStock
  /* eslint-enable no-unused-vars */
  id,
  invoiceUnit,
  isFavorite,
  isLoading,
  // eslint-disable-next-line no-unused-vars
  mainSku, // TODO: to remove ? issue mainSku > sku
  manufacturerSku,
  name,
  path,
  publicPrice,
  saleUnit,
  salesPackaging,
  sku,
  slug,
  sortables,
  splitPackaging,
  stockStatus,
  stocks,
  source,
  weeeAmount,
}) => {
  const { t } = useTranslation();
  const { user, isShowNetPrice } = useAuth();
  const { addToCart, isLoading: isLoadingAddToCart } = useAddToContext();
  const { defaultCart, refetchDefaultCart } = useDefaultCartContext();
  const location = useLocation();

  const { offsetWidth } = document.body;

  const linkRef = useRef(null);
  const quantityFieldRef = useRef(null);

  const [displayQuantity, setDisplayQuantity] = useState(false);

  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingAddToCart;
    const blocked = loading || disabled;

    return {
      addToCart: blocked || !user?.functionality_accesses?.carts,
      addToWishlist:
        blocked ||
        !user?.functionality_accesses?.wishlists ||
        !user?.functionality_accesses?.wishlists_add,
      disabled: blocked,
      loading,
    };
  }, [disabled, isLoading, isLoadingAddToCart, user]);

  /**
   * Return number of sortable elements
   * @returns {number}
   */
  const sortablesLength = useMemo(
    () => Object.keys(configurationsSortables).length,
    [configurationsSortables],
  );

  /**
   * Merge parent sortables and product sortables to have the same keys
   * @returns {array}
   */
  const newSortables = useMemo(
    () =>
      Object.keys({
        ...configurationsSortables,
        ...sortables,
      }).filter((sortable) => sortable in configurationsSortables),
    [configurationsSortables, sortables],
  );

  /**
   * Add to cart and display the quantity selector for mobile.
   * For mobile:
   * On first click, minimum quantity is added and the quantity selector is displayed
   * OnChange quantity has a debounce methode to add quantity after quantity adjustment.
   * addToCart come from AddToContext. refetchCart executed inside the addTo action of AddToContext
   * to update the cart in needed (refetchCart is by default noop function and is set only
   * in the cart use case
   * @type {function(): void}
   */
  const addToCartAction = useCallback(() => {
    if (offsetWidth < BREAKPOINTS.desktop) {
      setDisplayQuantity(true);
    }

    addToCart({
      items: [
        {
          quantity: quantityFieldRef.current.value,
          sku,
          name,
          id,
          brand,
          source,
        },
      ],
      cart: { ...defaultCart },
      onSuccessCallback: refetchDefaultCart,
      options: { ...addToCartOptions },
    });
  }, [
    addToCart,
    addToCartOptions,
    brand,
    defaultCart,
    id,
    name,
    offsetWidth,
    refetchDefaultCart,
    sku,
    source,
  ]);

  /**
   * Check if element is clickable as a link
   * Caution: add cursor pointer on clickable elements
   * @param {event} e
   */
  const handleClickBlock = useCallback(
    (e) =>
      addLinksToBlockElements({ e, link: linkRef.current, config: CONFIG }),
    [],
  );

  const getSortable = useCallback(
    (sortable) =>
      Object.keys(sortables).length > 0 &&
      sortables[sortable]?.value &&
      sortables[sortable]?.value !== NULL_VALUE
        ? `${sortables[sortable].value}${
            sortables[sortable]?.unit ? ` ${sortables[sortable].unit}` : ''
          }`
        : '',
    [sortables],
  );

  return (
    <div
      className={cn('product-table', className, {
        'product-table--show-net-price': isShowNetPrice,
        [`product-table--sortables-${sortablesLength}`]: sortablesLength > 0,
        active: location.pathname === `${ROUTE_PRODUCT}/${path}/${slug}`,
      })}
      data-qa-id="product-table"
      data-qa-sku={sku}
      onClick={handleClickBlock}
      onKeyPress={handleClickBlock}
      role="button"
      tabIndex="0"
    >
      <div className="product-table__sku">
        <div className="sku" data-qa-id="sku" data-qa-code={sku}>
          <span className="sku__label" data-qa-id="label">
            {t('common:entities:product.sku')}
          </span>
          <span className="sku__value" data-qa-id="value">
            {sku}
          </span>
        </div>
      </div>
      <div
        className="product-table__manufacturer-sku"
        data-qa-id="manufacturer-sku"
      >
        <div
          className="manufacturer-sku"
          data-qa-id="manufacturer-sku"
          data-qa-manufacturer-sku={manufacturerSku}
        >
          <span className="manufacturer-sku__label" data-qa-id="label">
            {t('common:entities:product.manufacturer_sku')}
          </span>
          <span className="manufacturer-sku__value" data-qa-id="value">
            {manufacturerSku}
          </span>
        </div>
      </div>
      <Stocks
        className="product-table__stock"
        companyAvailability={companyAvailability}
        noDesktopLabel
        status={stockStatus}
        stocks={stocks}
        unit={saleUnit}
      />
      <div className="product-table__name">
        <div className="name">
          <NavLink
            className="block-navlink"
            data-qa-id="product-name"
            ref={linkRef}
            to={`${ROUTE_PRODUCT}/${path}/${slug}`}
          >
            {name}
          </NavLink>
        </div>
      </div>
      {newSortables.map((sortable, index) => (
        <div
          className={cn(`product-table__sortable-${index + 1}`)}
          key={`sortable-${index + 1}`}
        >
          {getSortable(sortable)}
        </div>
      ))}
      <div
        className="product-table__public-price price"
        data-qa-id="public-price"
      >
        <span className="price__label">
          {t('common:entities:product.price.public_price')}
        </span>
        <Price value={publicPrice} unit={invoiceUnit} />
        {weeeAmount > 0 && (
          <div className="price__weee">
            {t('common:entities:product.price.weee_amount_long', {
              amount: (
                <Price
                  className="amount"
                  context="weee-amount"
                  key={`weee-amount-${weeeAmount}`}
                  sup={false}
                  value={weeeAmount}
                />
              ),
            })}
          </div>
        )}
      </div>
      {isShowNetPrice && (
        <div
          className="product-table__customer-price price"
          data-qa-id="customer-price"
        >
          <span className="price__label">
            {t('common:entities:product.price.customer_price')}
          </span>
          <Price
            className={cn({ 'price__value--old-price': discountPrice })}
            context={discountPrice ? 'old-price' : 'price'}
            emptyLabel={t('common:entities:product.price.not_price')}
            unit={invoiceUnit}
            value={customerPrice}
          />
          {discountPrice && (
            <Price
              className="price__value--discount"
              context="discount-price"
              value={discountPrice}
            />
          )}
          {weeeAmount > 0 && (
            <div className="price__weee">
              {t('common:entities:product.price.weee_amount_long', {
                amount: (
                  <Price
                    className="amount"
                    key={`weee-amount-${weeeAmount}`}
                    sup={false}
                    value={weeeAmount}
                    context="weee-amount"
                  />
                ),
              })}
            </div>
          )}
        </div>
      )}
      <ToggleWishlist
        className="product-table__favorite"
        disabled={isDisabled.addToWishlist}
        id={path}
        isFavorite={isFavorite}
        sku={sku}
      />
      <Quantity
        className={cn('product-table__quantity', { active: displayQuantity })}
        disabled={isDisabled.addToCart}
        id={id}
        isLoading={isDisabled.loading}
        onChange={addToCartAction}
        quantity={salesPackaging && !splitPackaging ? salesPackaging : 1}
        ref={quantityFieldRef}
        salesPackaging={salesPackaging}
        splitPackaging={splitPackaging}
        unit={saleUnit}
        updateOnChange={offsetWidth < BREAKPOINTS.desktop}
      />
      <div
        className={cn('product-table__cart', { active: !displayQuantity })}
        data-qa-id="product-table-cart"
      >
        <Button
          disabled={isDisabled.addToCart}
          context="add-to-cart"
          onClick={addToCartAction}
          title={t('common:messages:actions.add_to_cart')}
          variant="primary"
          value={sku}
        >
          <CartIcon className="icon" />
        </Button>
      </div>
    </div>
  );
};

Table.defaultProps = {
  ...TableDefaultProps,
};

Table.propTypes = {
  ...TablePropTypes,
};

export default withPriceAndStock(Table);
